/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import ReactPlayer from 'react-player';
import {makeStyles} from '@material-ui/core';
import Render from '@ion/dls/src/render/Render';
import {useMedia} from '../../hooks/useMedia';
import {NavigationIcon} from '../../icons/NavigationIcon';
import Hidden from '../../layout/Hidden';
import {ErrorState} from '../../notifications/ErrorState';
import {dlsColor} from '../../theme/theme';
import {ButtonSpinner} from '../loading/ButtonSpinner';
import useVideoPlayer from './hooks/useVideoPlayer';
import Duration from './Duration';
import FullScreen from './FullScreen';
import PlayPause from './PlayPause';
import ProgressControl from './ProgressControl';
import Volume from './Volume';

export interface VideoPlayerProps {
  onLeftNavigation?: Function;
  onRightNavigation?: Function;
  urls?: Array<String>;
  controls?: boolean
  showCarousal?: boolean;
  fullScreen?: boolean;
  setFullScreen?: Function;
  elementWidth?: number | string;
  isControls?: boolean;
  isFullScreen?: boolean;
  audio?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between'
  },
  controlAlign : {
    border          : `1px solid ${dlsColor('grey5')}`,
    backgroundColor : dlsColor('white'),
    width           : '100%'
  },
  miniControlAlign : {
    maxHeight       : 26,
    border          : `1px solid ${dlsColor('grey5')}`,
    backgroundColor : dlsColor('grey6'),
    marginTop       : '-1px'
  },
  parentDiv : {
    width          : '100%',
    display        : 'flex',
    alignItems     : 'center',
    flexDirection  : 'row',
    justifyContent : 'center'
  },
  videoplayer : {
    height         : '100%',
    minHeight      : '225px',
    width          : '100%',
    display        : 'flex',
    flexDirection  : 'column',
    justifyContent : 'center',
    alignItems     : 'center'

  },
  carousal : {
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center',
    width           : '45px',
    backgroundColor : dlsColor('grey6'),
    cursor          : 'pointer',
    margin          : '8px',
    height          : '43px'
  },
  loader : {
    display                        : 'flex',
    alignItems                     : 'center',
    justifyContent                 : 'center',
    width                          : ({elementWidth}: VideoPlayerProps) => `${elementWidth || '0px'}`,
    maxWidth                       : '924px',
    height                         : 'calc(100vh - 500px)',
    maxHeight                      : '260px',
    marginBottom                   : '8px',
    backgroundColor                : dlsColor('grey5'),
    flexGrow                       : 1,
    [theme.breakpoints.down('sm')] : {
      maxWidth : '300px'
    }
  }
}));

/**
 * @VideoPlayer
 *
 * Defines the Video Player Component. React-Player plugin is used for video player. It is to
 * display video recordings of asset.
 *
 * Figma link to refer Video Player component:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/02_DLS_Components?node-id=4060%3A43298
 *
 * ```typescript
 *
 * import VideoPlayer from '@ion/dls/src/components/videoplayer';
 *
 *   function handlePlayPause() {
 *     setPlaying(!playing);
 *   }
 *
 *  const [playing, setPlaying] = useState(false);
 *  const [volume, setVolume] = useState(0.8);
 *  const [fullScreen, setFullScreen] = useState(false);
 *
 *  //API called when video is in progress
 *  function handleProgress(state) {
 *    sliderChange(state.playedSeconds);
 *  };
 *
 *  //API To find the video duration
 *  function handleDuration(duration) {
 *   setDuration(duration);
 *  }
 *
 *  //API called when video ended
 *  function handleEnded() {
 *    setPlaying(false);
 *  }
 *
 *   <VideoPlayer urls={urls}
 *       fullScreen={fullScreen}
 *       setFullScreen={setFullScreen}
 *       isControls
 *       isFullScreen
 *       showCarousal />
 *
 * ```
*/
export const VideoPlayer = ({
  urls,
  controls,
  showCarousal,
  fullScreen,
  setFullScreen,
  elementWidth,
  isControls = true,
  isFullScreen,
  audio = false
}: VideoPlayerProps) => {
  const {isPhone} = useMedia();
  const [videoUrl, setVideoUrl]                   = useState(urls[0]);
  const [leftNavShow, setLeftNavShow]             = useState(urls.length > 1);
  const [rightNavShow, setRightNavShow]           = useState(urls.length > 1);
  const [fullScreenTracked, setFullScreenTracked] = useState(false);
  const [carousalIndex, setcarousalIndex]         = useState(0);
  const [isFull, setIsFull]                       = useState(false);
  const classes                                   = useStyles({fullScreenTracked, isPhone, elementWidth});
  const showLeftEventCarousal                     = leftNavShow && showCarousal && urls?.length > 1;
  const showRightEventCarousal                    = rightNavShow && showCarousal && urls?.length > 1;
  let playerRef                                   = null;

  const {
    handleVolumeChange,
    handleVolumeIcon,
    handlePlayPause,
    handleProgress,
    handleDuration,
    handleIsReady,
    handleEnded,
    handlePause,
    handlePlay,
    setFailed,
    progress,
    duration,
    isReady,
    restart,
    playing,
    failed,
    volume,
    retry
  } = useVideoPlayer();

  const handleFullScreenToggle = function() {
    setIsFull(!isFull);
  };

  useEffect(() => {
    setFullScreenTracked(fullScreen);
  }, []);

  function onLeftNavigation(e) {
    const newUrlsArr = [...urls];
    const prevIndex = carousalIndex-1;
    if (prevIndex >= 0) {
      const previousUrl= newUrlsArr[prevIndex];
      setVideoUrl(previousUrl);
      setcarousalIndex(prevIndex);
      if (prevIndex === 0) {
        setLeftNavShow(false);
      }
    }
    setRightNavShow(true);
  }

  function onRightNavigation(e) {
    const newUrlsArr = [...urls];
    const nextIndex = carousalIndex+1;
    const totalLength = urls.length-1;
    if (nextIndex <= totalLength) {
      const nextUrl= newUrlsArr[nextIndex];
      setVideoUrl(nextUrl);
      setcarousalIndex(nextIndex);
      if (nextIndex === totalLength) {
        setRightNavShow(false);
      }
    }
    setLeftNavShow(true);
  }

  function handleSeekChange(val) {
    playerRef?.seekTo(parseFloat(val));
  }

  function handleClickFullscreen() {
    setFullScreen(setFullScreenTracked(!fullScreenTracked));
    setFullScreen(!fullScreenTracked);
  }

  const ref = videoplayer => {
    playerRef = videoplayer;
  };

  const handleFullscreen = function() {
    const videoElement = playerRef?.getInternalPlayer();
    if (videoElement?.requestFullscreen) {
      videoElement?.requestFullscreen?.(); // Toggle fullscreen in Chrome and Firefox
    }
    if (videoElement?.webkitEnterFullScreen) {
      videoElement?.webkitEnterFullScreen?.(); // Toggle fullscreen in Safari for iOS
    }
  };

  const handleLeftPagination = useCallback(function(e) {
    onLeftNavigation(e);
  }, []);

  const handleRightPagination = useCallback(function(e) {
    onRightNavigation(e);
  }, []);

  if (!failed) {
    return (
      <div className={classes.parentDiv}>
        {showLeftEventCarousal && (
          <div style={{width: '70px'}}>
            <div className={classes.carousal}>
              <div onClick={handleLeftPagination}>
                <NavigationIcon direction='back' />
              </div>
            </div>
          </div>
        )}
        <div id='videoplayer'
          className={classes.videoplayer}>
          {!isReady && (
            <div className={classes.loader}>
              <ButtonSpinner />
            </div>
          )}
          <div style={{display: isReady ? 'block' : 'none'}}>
            <ReactPlayer url={videoUrl}
              ref={ref}
              playing={playing}
              duration={duration}
              volume={audio ? volume : 0}
              width={elementWidth || '100%'}
              style={{
                maxWidth : isPhone ? '300px' : '924px'
              }}
              fullscreen={isFull}
              height='auto'
              controls={controls}
              onProgress={handleProgress}
              onDuration={handleDuration}
              onReady={handleIsReady}
              onPause={handlePause}
              onPlay={handlePlay}
              onEnded={handleEnded}
              onError={function() {
                setFailed(true);
              }} />
          </div>
          {isControls && (
            <>
              <ProgressControl progress={progress}
                duration={duration}
                handleSeekChange={handleSeekChange} />
              <div className={`${classes.root} ${classes.controlAlign}`}>
                <div className={classes.root}>
                  <PlayPause size={28}
                    playing={playing}
                    restart={restart}
                    handlePlayPause={handlePlayPause} />
                  <Hidden hide='mobile'>
                    <Duration progress={progress}
                      duration={duration} />
                  </Hidden>
                </div>
                <div className={classes.root}>
                  <Hidden hide='mobile'>
                    <Render.If condition={audio}>
                      <Volume handleVolumeIcon={handleVolumeIcon}
                        handleVolumeChange={handleVolumeChange}
                        volume={volume} />
                    </Render.If>
                  </Hidden>
                  {isFullScreen && (
                    <FullScreen fullScreen={fullScreenTracked}
                      handleClickFullscreen={handleFullscreen}
                      size={20} />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        {showRightEventCarousal && (
          <div style={{width: '70px'}}>
            <div className={classes.carousal}>
              <div onClick={handleRightPagination}>
                <NavigationIcon direction='forward' />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className={classes.root}>
      <ErrorState onRefresh={retry} show />
    </div>
  );
};

export default VideoPlayer;
